import { createStyles } from '@mantine/core'
import React, {
  ReactElement,
  ReactNode,
  ReactNodeArray,
  useRef,
  useState,
} from 'react'

import {
  TAddressSearchItem,
  TAddressSearchOption,
} from '../AddressSearch/types'
import { BaseLayer } from './BaseLayer'
import { HoveredItems } from './HoveredItems'
import { MapControls } from './MapControls'
import { MapProvider } from './MapProvider'

export type TMapProps = {
  children?: ReactNode
  controls?: ReactNode[]
  /**
   * Extra elements to be rendered on in the map root container (dialogs, sidebars, etc.)
   */
  extraElements?: ReactNode
  useAddressSearchOptions?: (opts: {
    searchValue: string
  }) => TAddressSearchOption[]
  showAddressSearch?: boolean
  showCenterElements?: boolean
  extraSettingsForms?: ReactNode | ReactNodeArray
  addressSearchPlaceholder?: string
  onAddressSelected?: (address: TAddressSearchItem) => void
  bottomElement?: ReactNode
  viewControls?: ReactNode[]
}

const useStyle = createStyles((theme) => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowX: 'hidden',
  },
  mapRoot: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}))

/**
 * Latest version of openlayers map 2023
 * @param props
 * @returns
 */
export const Map = (props: TMapProps): ReactElement => {
  const {
    children,
    controls = [],
    extraElements,
    useAddressSearchOptions,
    showAddressSearch,
    showCenterElements,
    extraSettingsForms,
    addressSearchPlaceholder,
    onAddressSelected,
    bottomElement,
    viewControls,
  } = props

  const { classes } = useStyle()

  const refRoot = useRef<HTMLDivElement>(null)
  const [mapRoot, setMapRoot] = useState<HTMLDivElement | null>(null)

  return (
    <div className={classes.root} ref={refRoot}>
      {extraElements}
      <div className={classes.mapRoot} ref={(r) => setMapRoot(r)} />
      {mapRoot && (
        <MapProvider refMap={mapRoot}>
          <MapControls
            controls={controls}
            useAddressSearchOptions={useAddressSearchOptions}
            showAddressSearch={showAddressSearch}
            showCenterElements={showCenterElements}
            extraSettingsForms={extraSettingsForms}
            addressSearchPlaceholder={addressSearchPlaceholder}
            onAddressSelected={onAddressSelected}
            bottomElement={bottomElement}
            viewControls={viewControls}
          />
          <BaseLayer />
          <HoveredItems />
          {children}
        </MapProvider>
      )}
    </div>
  )
}
